import { useState, useRef, useEffect, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { cn } from '@/lib/utils';
import {
	Wallet,
	Clock,
	CircleDollarSign,
	Landmark,
	Activity,
	MoveRight,
	MoveLeft,
	Box,
	Hash
} from 'lucide-react'

import { formatUnixTimestamp, formatBalanceInSUI, formatBalanceInMIST } from '@/utils/format';

import { StatsCard } from '@/components/shared/StatsCard'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'

import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
} from '@/components/ui/card'
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '@/components/ui/tabs'
import {
	Pagination,
	PaginationContent,
	PaginationEllipsis,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious,
} from '@/components/ui/pagination'

import {
	useAccountsDetails,
	useAccountBalances,
	useAccountFromTransactionBlocks,
	useAccountToTransactionBlocks
} from '@/hooks/useApi'

const PAGINATION_ITEM_COUNT = 5;
const PAGE_SIZE = 10;

const clamp = (num: number, min: number, max: number) => {
	return Math.min(Math.max(num, min), max)
}

interface FromTransactionsTabProps { id: string };
const FromTransactionsTab = (props: FromTransactionsTabProps) => {
	const { id } = props;
	if (!id) {
		return <> Not Found </>;
	}

	const initialLoadCompleted = useRef<boolean>(false);

	const startingCursor = useRef<string | null>(null);
	const startingPageData = useRef<any>(null);

	const [cursor, setCursor] = useState<string | null>(null);

	const [pageToRender, setPageToRender] = useState<any>({});
	const [prevCursorHistory, setPrevCursorHistory] = useState<Array<string>>([]);
	const nextCursor = useRef<string | null | undefined>(null);

	const { data: accountFromTransactionBlocks } = useAccountFromTransactionBlocks(id, PAGE_SIZE, cursor);

	useEffect(() => {
		if (!initialLoadCompleted.current && accountFromTransactionBlocks?.hasNextPage) {
			startingCursor.current = accountFromTransactionBlocks.cursor;
			startingPageData.current = accountFromTransactionBlocks;
			initialLoadCompleted.current = true;
		}

		if (accountFromTransactionBlocks?.hasNextPage) {
			nextCursor.current = accountFromTransactionBlocks.cursor;
		}

		if (startingCursor.current === nextCursor.current) {
			setPageToRender(startingPageData.current);
		} else {
			setPageToRender(accountFromTransactionBlocks);
		}
	}, [accountFromTransactionBlocks])

	if (!accountFromTransactionBlocks) {
		return <>Loading...</>
	}

	return (
		<>
			<Pagination className='mb-2'>
				<PaginationContent>
					{/* Previous Button */}
					<PaginationItem>
						<PaginationPrevious
							className='hover:bg-[#C0E6FF]/10 hover:backdrop-blur-md'
							content={<span className='text-xs'>Prev</span> as any}
							onClick={() => {
								if (prevCursorHistory.length === 1) {
									setCursor(null);
								}

								setPrevCursorHistory((prev: any) => {
									const temp = [...prev];
									temp.pop();
									if (temp.length) setCursor(temp[temp.length - 1]);
									return temp;
								})
							}}
						/>
					</PaginationItem>

					{/* Next Button */}
					<PaginationItem>
						<PaginationNext
							className='hover:bg-[#C0E6FF]/10 hover:backdrop-blur-md'
							content={<span className='text-xs'>Next</span> as any}
							onClick={() => {
								if (!nextCursor.current) return;
								setCursor(nextCursor.current);
								setPrevCursorHistory((prev: any) => [...prev, nextCursor.current]);
							}}
						/>
					</PaginationItem>
				</PaginationContent>
			</Pagination>
			<div className="rounded-lg border">
				<Table>
					<TableHeader className='bg-[#C0E6FF]/10 shadow-lg'>
						<TableRow className='pointer-events-none'>
							<TableHead className='text-white'>
								<div className='flex flex-row items-center'>
									<div className='flex-1 text-xs font-medium'>Checkpoint</div>
								</div>
							</TableHead>
							<TableHead className='text-white'>
								<div className='flex flex-row items-center'>
									<div className='flex-1 text-xs font-medium'>Digest</div>
								</div>
							</TableHead>
							<TableHead className='text-white'>
								<div className='flex flex-row items-center'>
									<div className='flex-1 text-xs font-medium'>Timestamp</div>
								</div>
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{accountFromTransactionBlocks?.data.map((transactionBlock: any) => (
							<TableRow key={transactionBlock.digest} className='hover:bg-[#C0E6FF]/10 hover:backdrop-blur-md'>
								<TableCell className='text-xs'>{Number(transactionBlock.checkpoint).toLocaleString()}</TableCell>
								<TableCell className='text-xs'>
									<Link to={`/transaction-blocks/${transactionBlock.digest}`} className="flex items-center space-x-2 text-[#4DA2FF] hover:text-[#C0E6FF]">
										<span className='hidden md:block'>{`${transactionBlock.digest.slice(0, 6)}...${transactionBlock.digest.slice(-6)}`}</span>
										<span className='md:hidden'>{`${transactionBlock.digest.slice(0, 6)}...`}</span>
									</Link>
								</TableCell>
								<TableCell className='text-xs'>{formatUnixTimestamp(Number(transactionBlock.timestampMS))}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
		</>

	)
}

interface ToTransactionsTabProps { id: string };
const ToTransactionsTab = (props: ToTransactionsTabProps) => {
	const { id } = props;
	if (!id) {
		return <> Not Found </>;
	}

	const initialLoadCompleted = useRef<boolean>(false);

	const startingCursor = useRef<string | null>(null);
	const startingPageData = useRef<any>(null);

	const [cursor, setCursor] = useState<string | null>(null);

	const [pageToRender, setPageToRender] = useState<any>({});
	const [prevCursorHistory, setPrevCursorHistory] = useState<Array<string>>([]);
	const nextCursor = useRef<string | null | undefined>(null);

	const { data: accountToTransactionBlocks } = useAccountToTransactionBlocks(id, PAGE_SIZE, cursor);

	useEffect(() => {
		if (!initialLoadCompleted.current && accountToTransactionBlocks?.hasNextPage) {
			startingCursor.current = accountToTransactionBlocks.cursor;
			startingPageData.current = accountToTransactionBlocks;
			initialLoadCompleted.current = true;
		}

		if (accountToTransactionBlocks?.hasNextPage) {
			nextCursor.current = accountToTransactionBlocks.cursor;
		}

		if (startingCursor.current === nextCursor.current) {
			setPageToRender(startingPageData.current);
		} else {
			setPageToRender(accountToTransactionBlocks);
		}
	}, [accountToTransactionBlocks])

	if (!accountToTransactionBlocks) {
		return <>Loading...</>
	}

	return (
		<>
			<Pagination className='mb-2'>
				<PaginationContent>
					{/* Previous Button */}
					<PaginationItem>
						<PaginationPrevious className='hover:bg-[#C0E6FF]/10 hover:backdrop-blur-md'
							content={<span className='text-xs'>Prev</span> as any}
							onClick={() => {
								if (prevCursorHistory.length === 1) {
									setCursor(null);
								}

								setPrevCursorHistory((prev: any) => {
									const temp = [...prev];
									temp.pop();
									if (temp.length) setCursor(temp[temp.length - 1]);
									return temp;
								})
							}}
						/>
					</PaginationItem>

					{/* Next Button */}
					<PaginationItem>
						<PaginationNext className='hover:bg-[#C0E6FF]/10 hover:backdrop-blur-md'
							content={<span className='text-xs'>Prev</span> as any}
							onClick={() => {
								if (!nextCursor.current) return;
								setCursor(nextCursor.current);
								setPrevCursorHistory((prev: any) => [...prev, nextCursor.current]);
							}}
						/>
					</PaginationItem>
				</PaginationContent>
			</Pagination>
			<div className="rounded-lg border">
				<Table>
					<TableHeader className='bg-[#C0E6FF]/10 shadow-lg'>
						<TableRow className='pointer-events-none'>
							<TableHead className='text-white'>
								<div className='flex flex-row items-center'>
									<div className='flex-1 text-xs font-medium'>Checkpoint</div>
								</div>
							</TableHead>
							<TableHead className='text-white'>
								<div className='flex flex-row items-center'>
									<div className='flex-1 text-xs font-medium'>Digest</div>
								</div>
							</TableHead>
							<TableHead className='text-white'>
								<div className='flex flex-row items-center'>
									<div className='flex-1 text-xs font-medium'>Timestamp</div>
								</div>
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{accountToTransactionBlocks?.data.map((transactionBlock: any) => (
							<TableRow key={transactionBlock.digest} className='hover:bg-[#C0E6FF]/10 hover:backdrop-blur-md'>
								<TableCell className='text-xs'>{Number(transactionBlock.checkpoint).toLocaleString()}</TableCell>
								<TableCell className='text-xs'>
									<Link to={`/transaction-blocks/${transactionBlock.digest}`} className="flex items-center space-x-2 text-[#4DA2FF] hover:text-[#C0E6FF]">
										<span className='hidden md:block'>{`${transactionBlock.digest.slice(0, 6)}...${transactionBlock.digest.slice(-6)}`}</span>
										<span className='md:hidden'>{`${transactionBlock.digest.slice(0, 6)}...`}</span>
									</Link>
								</TableCell>
								<TableCell className='text-xs'>{formatUnixTimestamp(Number(transactionBlock.timestampMS))}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
		</>
	)
}

interface TransactionsTabProps { id: string }
function TransactionsTab(props: TransactionsTabProps) {
	const { id } = props;
	if (!id) {
		return <> Not Found </>;
	}

	return (
		<div className="space-y-4 py-4">
			<Tabs defaultValue="from" className="w-full">
				<TabsList className="w-full sm:w-auto bg-[#C0E6FF]/10 backdrop-blur-md border border-white/20 rounded-md shadow-lg">
					<TabsTrigger value="from" className="flex-1 sm:flex-none">
						From Address
					</TabsTrigger>
					<TabsTrigger value="to" className="flex-1 sm:flex-none">
						To Address
					</TabsTrigger>
				</TabsList>

				<TabsContent value="from" className="mt-6">
					<Card className='bg-transparent'>
						<CardHeader className="flex flex-col justify-start space-y-0 p-0 min-w-60">
							<div className='flex flex-row items-center px-2 pt-2'>
								<div className="rounded-full px-2 transition-colors">
									<MoveRight className='h-5 w-5' />
								</div>
								<div className='flex-1'>
									<CardTitle className="text-sm md:text-md lg:text-lg font-medium">From Activity</CardTitle>
								</div>
							</div>
							<div className='px-4'>
								<CardDescription className='text-xs md:text-sm lg:text-md'>
									Transactions from this address
								</CardDescription>
							</div>
						</CardHeader>
						<CardContent className='px-0'>
							<FromTransactionsTab id={id} />
						</CardContent>
					</Card>
				</TabsContent>

				<TabsContent value="to" className="mt-6">
					<Card className='bg-transparent'>
						<CardHeader className="flex flex-col justify-start space-y-0 p-0 min-w-60">
							<div className='flex flex-row items-center px-2 pt-2'>
								<div className="rounded-full px-2 transition-colors">
									<MoveLeft className='h-5 w-5' />
								</div>
								<div className='flex-1'>
									<CardTitle className="text-sm md:text-md lg:text-lg font-medium">To Activity</CardTitle>
								</div>
							</div>
							<div className='px-4'>
								<CardDescription className='text-xs md:text-sm lg:text-md'>
									Transactions to this address
								</CardDescription>
							</div>
						</CardHeader>
						<CardContent className='px-0'>
							<ToTransactionsTab id={id} />
						</CardContent>
					</Card>
				</TabsContent>
			</Tabs>
		</div>
	);
}

interface CoinsTabProps {
	data: any;
}

function CoinsTab(props: CoinsTabProps) {
	const { data } = props;

	const [currentPage, setCurrentPage] = useState<number>(1);
	const [startAtPage, setStartAtPage] = useState<number>(1);

	const totalPages = useMemo(() => {
		if (data) {
			return Math.ceil(data?.length / PAGE_SIZE);
		}
		return 0;
	}, [data, PAGE_SIZE])

	const pageRange = useMemo(() => {
		if (!totalPages) return null;

		// if the page count is less than twice the PAGINATION_ITEM_COUNT (5), just list them all
		if (totalPages < (PAGINATION_ITEM_COUNT * 2)) {
			return [...Array(totalPages).keys()].map((i: number) => i + 1)
		}

		// if the page count is less than twice the PAGINATION_ITEM_COUNT (5), special render
		if (totalPages === (PAGINATION_ITEM_COUNT * 2)) {
			return [...Array(PAGINATION_ITEM_COUNT).keys()].map((i: number) => i + startAtPage)
		}

		// remainder render
		if ((totalPages % PAGINATION_ITEM_COUNT) && startAtPage === totalPages - (totalPages % PAGINATION_ITEM_COUNT) + 1) {
			return [...Array(totalPages % PAGINATION_ITEM_COUNT).keys()].map((i: number) => i + startAtPage)
		}

		return [...Array(PAGINATION_ITEM_COUNT).keys()].map((i: number) => i + startAtPage)

	}, [currentPage, startAtPage, PAGINATION_ITEM_COUNT, totalPages])

	return (
		<>
			<Pagination className='mb-2'>
				<PaginationContent>
					{/* Previous Button */}
					{totalPages >= PAGINATION_ITEM_COUNT * 2 && <PaginationItem>
						<PaginationPrevious
							className='hover:bg-[#C0E6FF]/10 hover:backdrop-blur-md'
							content={<span className='text-xs'>Prev</span> as any}
							onClick={() => {
								// determine if we need to adjust the page range (paging backward)
								if ((currentPage - 1) % PAGINATION_ITEM_COUNT === 0) {
									setStartAtPage(clamp(currentPage - PAGINATION_ITEM_COUNT, 1, totalPages))
								}
								setCurrentPage(clamp(currentPage - 1, 1, totalPages))
							}}
						/>
					</PaginationItem>}
					{/* First Page Button */}
					{totalPages > PAGINATION_ITEM_COUNT * 2 && startAtPage !== 1 && <>
						<PaginationItem
							key={1}
						>
							<PaginationLink
								className='hover:bg-[#C0E6FF]/10 hover:backdrop-blur-md'
								isActive={1 === currentPage}
								onClick={() => {
									setCurrentPage(1);
									setStartAtPage(1);
								}}
							><span className='text-xs'>{1}</span></PaginationLink>
						</PaginationItem>
						<PaginationItem>
							<PaginationEllipsis />
						</PaginationItem>
					</>}
					{/* Page Buttons */}
					{pageRange && pageRange.map((page: number) => (
						<PaginationItem
							key={page}
						>
							<PaginationLink
								className={
									cn(`hover:bg-[#C0E6FF]/10 hover:backdrop-blur-md ${page === currentPage ? 'bg-[#C0E6FF]/10 backdrop-blur-md' : ''}`)}
								onClick={() => {
									setCurrentPage(clamp(page, 1, totalPages))
								}}
							><span className='text-xs'>{page}</span></PaginationLink>
						</PaginationItem>
					))}

					{/* Last Page Button + Separator */}
					{totalPages >= PAGINATION_ITEM_COUNT * 2 && startAtPage <= (totalPages - PAGINATION_ITEM_COUNT) && <>
						<PaginationItem>
							<PaginationEllipsis />
						</PaginationItem>
						<PaginationItem
							key={totalPages}
						>
							<PaginationLink
								className='hover:bg-[#C0E6FF]/10 hover:backdrop-blur-md'
								isActive={totalPages === currentPage}
								onClick={() => {
									setCurrentPage(totalPages);
									setStartAtPage(totalPages - PAGINATION_ITEM_COUNT + 1);
								}}
							><span className='text-xs'>{totalPages}</span></PaginationLink>
						</PaginationItem>
					</>}

					{/* Next Button */}
					{totalPages >= PAGINATION_ITEM_COUNT * 2 && <PaginationItem>
						<PaginationNext
							className='hover:bg-[#C0E6FF]/10 hover:backdrop-blur-md'
							content={<span className='text-xs'>Prev</span> as any}
							onClick={() => {
								if (totalPages === currentPage) return;

								// determine if we need to adjust the page range (paging forward)
								if (currentPage % PAGINATION_ITEM_COUNT === 0) {
									setStartAtPage(clamp(currentPage + 1, 1, totalPages));
								}
								setCurrentPage(clamp(currentPage + 1, 1, totalPages));
							}}
						/>
					</PaginationItem>}
				</PaginationContent>
			</Pagination>
			<div className="rounded-lg border">
				<Table>
					<TableHeader className='bg-[#C0E6FF]/10 shadow-lg'>
						<TableRow className='pointer-events-none'>
							<TableHead className='text-white'>
								<div className='flex flex-row items-center'>
									<div className='flex-1 text-xs font-medium'>Coin</div>
								</div>
							</TableHead>
							<TableHead className='text-white'>
								<div className='flex flex-row items-center'>
									<div className='flex-1 text-xs font-medium'>Balance</div>
								</div>
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{data.filter((element: any) => element.coinType !== '0x2::sui::SUI').slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map((balance: any) => (
							<TableRow key={balance.coinType} className='hover:bg-[#C0E6FF]/10 hover:backdrop-blur-md'>
								<TableCell className="font-mono text-xs">
									<span className='hidden lg:block'>{balance.coinType}</span>
									<span className='lg:hidden'>{`...${balance.coinType.slice(-12)}`}</span>
								</TableCell>
								<TableCell className="font-medium text-xs">
									{Number(balance.totalBalance).toLocaleString()}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
		</>

	)
}

export const AccountsDetailsPage = () => {
	const { id } = useParams<{ id: string }>()
	if (!id) {
		return <>Not Found</>
	}

	const { data: accountDetails } = useAccountsDetails(id)
	const { data: accountBalances } = useAccountBalances(id);

	if (!accountDetails || !accountBalances) {
		return <>Loading...</>
	}

	return (
		<div className="flex flex-col overflow-hidden w-full h-full">
			<div className='flex-1 overflow-y-auto overflow-x-hidden flex max-w-full 
  			[&::-webkit-scrollbar-track]:bg-gray-100
  			[&::-webkit-scrollbar-thumb]:bg-gray-300
  			dark:[&::-webkit-scrollbar-track]:bg-neutral-700
  			dark:[&::-webkit-scrollbar-thumb]:bg-neutral-500'
			>
				<div className="flex flex-col w-full h-full gap-6 px-2 lg:px-4 p-4">
					<h1 className="scroll-m-20 text-md lg:text-2xl font-bold tracking-tight">
						Account Details
					</h1>
					<div className='grid grid-cols-1 lg:grid-cols-4 gap-2'>
						<div className='lg:col-span-3'>
							<StatsCard
								title="Address"
								className='text-sm lg:grid-cols-3'
								valueStyleOverride='text-lg overflow-hidden whitespace-nowrap text-ellipsis'
								value={accountDetails.address}
								icon={<Wallet className="text-primary h-4 w-4" />}
							/>
						</div>
						<div className='lg:col-span-1'>
							<StatsCard
								title="SUI Balance"
								className='text-sm'
								valueStyleOverride='text-md xl:text-xl'
								value={
									<>
										<div className='text-md'>
											{accountDetails?.suiBalance.totalBalance ? formatBalanceInSUI(Number(accountDetails?.suiBalance.totalBalance)) : '--'} SUI
										</div>
										<div className='text-sm text-muted-foreground'>
											{accountDetails?.suiBalance.totalBalance ? formatBalanceInMIST(Number(accountDetails?.suiBalance.totalBalance)) : '--'} MIST
										</div>
									</>
								}
								icon={<CircleDollarSign className="text-primary h-4 w-4" />}
							/>
						</div>

					</div>

					<Tabs defaultValue="holdings" className="w-full">
						<TabsList
							className="
								w-full sm:w-auto bg-[#C0E6FF]/10 backdrop-blur-md border border-white/20 rounded-md shadow-lg
							"
						>
							<TabsTrigger value="holdings" className="flex-1 sm:flex-none">
								Holdings
							</TabsTrigger>
							<TabsTrigger value="activity" className="flex-1 sm:flex-none">
								Activity
							</TabsTrigger>
						</TabsList>

						<TabsContent value="holdings" className="mt-4 ">
							<Card className='bg-transparent'>
								<CardHeader className="flex flex-col justify-start space-y-0 p-0 min-w-60">
									<div className='flex flex-row items-center px-2 pt-2'>
										<div className="rounded-full px-2 transition-colors">
											<Landmark className='h-4 w-4' />
										</div>
										<div className='flex-1'>
											<CardTitle className="text-sm md:text-md lg:text-lg font-medium">Holdings</CardTitle>
										</div>
									</div>
									<div className='px-4'>
										<CardDescription className='text-xs lg:text-md'>
											Coin balances for this address
										</CardDescription>
									</div>
								</CardHeader>
								<CardContent className='px-2'>
									<CoinsTab data={accountBalances} />
								</CardContent>
							</Card>
						</TabsContent>

						<TabsContent value="activity" className="mt-6">
							<Card className='bg-transparent'>
								<CardHeader className="flex flex-col justify-start space-y-0 p-0 min-w-60">
									<div className='flex flex-row items-center px-2 pt-2'>
										<div className="rounded-full px-2 transition-colors">
											<Activity className='h-4 w-4' />
										</div>
										<div className='flex-1'>
											<CardTitle className="text-sm md:text-md lg:text-lg font-medium">Activity</CardTitle>
										</div>
									</div>
									<div className='px-4'>
										<CardDescription className='text-xs lg:text-md'>
											Transaction activity for this address
										</CardDescription>
									</div>
								</CardHeader>
								<CardContent className='px-2'>
									<TransactionsTab id={id} />
								</CardContent>
							</Card>
						</TabsContent>
					</Tabs>
				</div>
			</div>
		</div>
	)
}